// src/components/opportunity/OpportunityDocumentsTab.tsx
//
// Documents tab — upload sections driven by manager document slots.

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Opportunity } from "@/types/opportunity";
import { resolveManagerPresetFormatKey } from "@/lib/opportunity/formatRegistry";
import { resolveFormatDocumentSlots } from "@/lib/opportunity/formatDocumentSlotResolver";
import type { FormatDocumentSlotDef } from "@/lib/opportunity/formatDocumentSlotTypes";
import {
  listOpportunityDocuments,
  type OpportunityDocument,
} from "@/lib/opportunity/opportunityDocumentsClient";
import { OpportunityDocumentsCategorySection } from "@/components/opportunity/OpportunityDocumentsCategory";
import {
  SIGNED_CONTRACT_DOCUMENT_TYPE,
  SIGNED_CONTRACT_SLOT_KEY,
} from "@/lib/opportunity/signedContractDocumentSlot";

export type OpportunityDocumentsTabProps = {
  opportunity: Opportunity;
  /** When set, scroll/highlight this slot (e.g. signed_contract from Generated). */
  focusSlotKey?: string | null;
  /** Optional generated-output id to attach when uploading into the focused slot. */
  sourceGeneratedOutputId?: string | null;
};

export function OpportunityDocumentsTab({
  opportunity,
  focusSlotKey = null,
  sourceGeneratedOutputId = null,
}: OpportunityDocumentsTabProps) {
  const [documents, setDocuments] = useState<OpportunityDocument[]>([]);
  const [slots, setSlots] = useState<FormatDocumentSlotDef[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  const presetFormatKey = resolveManagerPresetFormatKey(opportunity.product_type);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void Promise.all([
      listOpportunityDocuments({ opportunityId: opportunity.id, signMedia: true }),
      resolveFormatDocumentSlots({
        formatKey: presetFormatKey,
        productType: opportunity.product_type,
      }),
    ]).then(([docsR, slotDefs]) => {
      if (cancelled) return;
      if (docsR.ok) {
        setDocuments(docsR.data.documents ?? []);
      } else {
        setError(docsR.error);
        setDocuments([]);
      }
      setSlots(slotDefs);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [opportunity.id, opportunity.product_type, presetFormatKey, tick]);

  const byCategory = useMemo(() => {
    const map = new Map<string, OpportunityDocument[]>();
    for (const slot of slots) {
      map.set(slot.slot_key, []);
    }
    for (const d of documents) {
      const bucket = map.get(d.category);
      if (bucket) {
        bucket.push(d);
      } else {
        const extra = map.get("__legacy__") ?? [];
        extra.push(d);
        map.set("__legacy__", extra);
      }
    }
    return map;
  }, [documents, slots]);

  const legacyDocs = byCategory.get("__legacy__") ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-600 dark:text-zinc-400">
          Uploaded and signed files for this opportunity (manager-configured slots in Proposal
          formats). Created PDF packages live under <strong>Generated documents</strong> — after
          signature, upload the signed file here
          {focusSlotKey ? (
            <>
              {" "}
              (suggested slot: <code className="text-[11px]">{focusSlotKey}</code>)
            </>
          ) : null}
          .
        </p>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="px-2.5 py-1 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          {error}
        </div>
      ) : null}

      {slots.map((slot) => {
        const isFocus = focusSlotKey === slot.slot_key;
        return (
          <OpportunityDocumentsCategorySection
            key={slot.slot_key}
            opportunityId={opportunity.id}
            category={slot.slot_key}
            label={slot.label}
            helperText={slot.helper_text ?? ""}
            documents={byCategory.get(slot.slot_key) ?? []}
            busy={loading}
            onUploaded={refresh}
            requiredBeforeSigned={slot.required_before_signed}
            highlighted={isFocus}
            sourceGeneratedOutputId={isFocus ? sourceGeneratedOutputId : null}
            suggestedDocumentType={
              isFocus && slot.slot_key === SIGNED_CONTRACT_SLOT_KEY
                ? SIGNED_CONTRACT_DOCUMENT_TYPE
                : null
            }
          />
        );
      })}

      {legacyDocs.length > 0 ? (
        <OpportunityDocumentsCategorySection
          opportunityId={opportunity.id}
          category="legacy"
          label="Other documents"
          helperText="Documents uploaded before slot configuration changed."
          documents={legacyDocs}
          busy={loading}
          onUploaded={refresh}
          readOnly
        />
      ) : null}
    </div>
  );
}
